import { useCallback, useState } from 'react';

// Deep links into the replay: ?tab=replay&round=7&t=42. `round` is 1-based and `t` is seconds
// since the round went live (the replay's frame 0). Bad values fall back instead of failing.
export const parseReplayRound = (value: string | null): number => {
    const n = Number(value);
    return Number.isInteger(n) && n >= 1 ? n : 1;
};

export const parseReplayTime = (value: string | null): number | null => {
    if (value === null || value.trim() === '') return null;
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? n : null;
};

// Where the replay tab opens. The page owns it so it survives the viewer unmounting when you
// switch tabs, and so the URL can follow it.
export interface ReplayPosition {
    round: number;
    time: number | null;
    // Opens 3 s before this player's first kill in the round (clip -> "View in 2D").
    focusSteamId: string | null;
}

export function useReplayPosition(searchParams: URLSearchParams) {
    const [position, setPosition] = useState<ReplayPosition>(() => ({
        round: parseReplayRound(searchParams.get('round')),
        time: parseReplayTime(searchParams.get('t')),
        focusSteamId: null,
    }));

    // From the viewer. A new round makes the linked moment stale, so it is dropped.
    const onRoundChange = useCallback((round: number) => {
        setPosition(p => (p.round === round ? p : { round, time: null, focusSteamId: null }));
    }, []);

    const openAt = useCallback((round: number, focusSteamId: string | null = null) => {
        setPosition({ round, time: null, focusSteamId });
    }, []);

    return { position, onRoundChange, openAt };
}

// Round 1 is the default, so it is left out, the same way ?tab=overview is.
export function writeReplayParams(params: URLSearchParams, onReplayTab: boolean, position: ReplayPosition) {
    if (onReplayTab && position.round > 1) params.set('round', String(position.round));
    else params.delete('round');
    if (onReplayTab && position.time !== null) params.set('t', String(position.time));
    else params.delete('t');
}
